/**
 * Choix du palier applicable, partagé par la ligne en saisie et par le bandeau de
 * changement de société.
 *
 * C'est tout ce que le JavaScript sait faire des règles tarifaires : les prix, les taux
 * cumulés et les libellés arrivent résolus du serveur, et aucune règle de cascade n'est
 * réécrite ici. Voir docs/DRI/Phase1/regles-tarifaires.md, § 7.2.
 */

/** Les décimales d'un prix se gardent, celles d'une quantité non. */
function number(value, decimals) {
    const fixed = (parseFloat(value) || 0).toFixed(decimals);

    return (decimals > 2 ? fixed.replace(/\.?0+$/, '') : fixed).replace('.', ',');
}

/**
 * Une ligne forfaitaire issue d'une étude ne porte ni la quantité ni le prix unitaire de
 * l'article : ses seuils ont été contrôlés sur l'étude. Même règle que SaleLimitGuard.
 */
export function isLumpSum(line) {
    return String(line.unit || '').trim().toLowerCase() === 'forfait';
}

/** Le prix réellement payé à l'unité, celui que le plancher contrôle. */
export function netUnitPrice(amount, discount) {
    return Math.round(((parseFloat(amount) || 0) * (1 - (parseFloat(discount) || 0) / 100)) * 100) / 100;
}

/**
 * Ce qui refuse une ligne au regard des seuils de son article, ou une chaîne vide.
 */
export function limitBreach(line) {
    if (!line || line.is_title || line.is_eco_participation || !line.article_id || isLumpSum(line)) {
        return '';
    }

    const reasons = [];
    const minimumQuantity = parseFloat(line.minimum_order_quantity);
    const quantity = parseFloat(line.quantity) || 0;

    if (Number.isFinite(minimumQuantity) && quantity < minimumQuantity) {
        reasons.push('Quantité minimale de commande : ' + number(minimumQuantity, 3) + '.');
    }

    const floor = parseFloat(line.minimum_selling_price);
    const net = netUnitPrice(line.amount, line.discount);

    if (Number.isFinite(floor) && net < floor) {
        reasons.push('Prix net de ' + number(net, 2) + ' € sous le prix plancher de ' + number(floor, 2) + ' €.');
    }

    // Les deux conditions se disent ensemble : corriger l'une ne doit pas révéler l'autre.
    return reasons.join(' ');
}

/**
 * Le franchissement qui bloque encore. Un motif ne couvre que celui pour lequel il a été
 * donné, sinon il servirait de laissez-passer permanent.
 */
export function blockingBreach(line) {
    const breach = limitBreach(line);

    if (!breach || (line.limit_override_for === breach && line.limit_override_reason)) {
        return '';
    }

    return breach;
}

/** Le dernier seuil atteint, ou le repli en deçà du premier. */
export function tierFor(pricing, quantity) {
    if (!pricing) {
        return null;
    }

    var wanted = parseFloat(quantity);
    if (!Number.isFinite(wanted)) {
        wanted = 1;
    }

    var reached = (pricing.tiers || []).filter(function (tier) {
        return parseFloat(tier.from) <= wanted;
    });

    return reached.length ? reached[reached.length - 1] : (pricing.fallback || null);
}

/**
 * La remise d'un article chez une société, à une quantité donnée : le palier s'il y a une
 * règle, le taux de la grille sinon, et aucune quand le client n'a droit à rien. Le prix
 * unitaire n'en fait jamais partie.
 */
export function resolveEntry(entry, quantity) {
    if (entry && entry.pricing) {
        return tierFor(entry.pricing, quantity);
    }

    if (entry && entry.discount !== null && entry.discount !== undefined) {
        return { discount: parseFloat(entry.discount), field: 'discount', label: entry.discount_label };
    }

    return { discount: 0, field: null, label: null };
}
